import { useParams, Link } from "react-router-dom";
import { ArrowRight, Phone, MapPin, CalendarDays, FileText, Check } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import SectionHeading from "../components/SectionHeading";
import CTABanner from "../components/CTABanner";
import { useSEO } from "../hooks/useSEO";
import { communesIndex, villes } from "../data/villes";
import { site } from "../data/site";
import { services } from "../data/services";
import NotFound from "./NotFound";

export default function LocalPage() {
  const { slug } = useParams();
  if (slug && slug.startsWith("jardinier-")) {
    const info = communesIndex[slug.slice("jardinier-".length)];
    if (info) return <CommuneView info={info} />;
  }
  return <NotFound />;
}

function CommuneView({ info }) {
  const ville = villes[info.ville];
  useSEO({
    title: `Jardinier à ${info.name} — Entretien de jardins, haies et pelouses | Embek Group`,
    description: `Embek Group, votre jardinier à ${info.name} (${ville.name}) : entretien de jardins, tonte de pelouse, taille de haies, aménagement paysager, nettoyage extérieur et vide-cave. Devis gratuit et rendez-vous en ligne.`,
    path: `/jardinier-${info.slug}`,
  });

  return (
    <>
      <PageHeader
        eyebrow={`Zone d'intervention — ${ville.name}`}
        title={`Jardinier à ${info.name}`}
        description={`Entretien de jardins, tailles, pelouses, aménagement paysager, nettoyage extérieur et vide-cave à ${info.name} et aux alentours — avec professionnalisme, précision et passion.`}
      />

      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <SectionHeading eyebrow={`Embek Group à ${info.name}`} title={`Un jardinier de confiance, à ${info.name}`} />
              <Reveal delay={0.15}>
                <p className="mt-6 text-coal-muted font-light leading-relaxed">
                  Vous habitez {info.name} ? Embek Group assure l'entretien de vos jardins et espaces verts :
                  tonte de pelouse, désherbage, taille de haies et d'arbustes, entretien des massifs, nettoyage
                  extérieur, remise en état de terrains et vide-cave.
                </p>
                <p className="mt-4 text-coal-muted font-light leading-relaxed">
                  Notre équipe intervient à {info.name} et dans les communes voisines de la région de {ville.name},
                  pour une intervention ponctuelle ou un contrat d'entretien régulier. Demandez votre devis gratuit
                  ou réservez votre créneau en ligne.
                </p>
              </Reveal>
              <Reveal delay={0.2}>
                <div className="mt-8 flex flex-col sm:flex-row gap-4">
                  <a href={site.phoneHref} data-testid={`commune-phone-${info.slug}`} className="btn-primary">
                    <Phone size={15} /> Appeler
                  </a>
                  <Link to="/demander-un-devis" data-testid={`commune-devis-${info.slug}`} className="btn-outline-forest">
                    <FileText size={15} /> Demander un devis
                  </Link>
                  <Link to="/rendez-vous" data-testid={`commune-rdv-${info.slug}`} className="btn-outline-forest">
                    <CalendarDays size={15} /> Prendre rendez-vous
                  </Link>
                </div>
              </Reveal>
              <Reveal delay={0.25}>
                <Link
                  to={`/${ville.slug}`}
                  data-testid={`commune-city-link-${info.slug}`}
                  className="inline-flex items-center gap-2 mt-7 text-sm font-semibold text-forest underline underline-offset-4 decoration-moss/50"
                >
                  <MapPin size={14} className="text-moss" /> Découvrir nos interventions à {ville.name}
                </Link>
              </Reveal>
            </div>

            <Reveal delay={0.1}>
              <div className="rounded-3xl overflow-hidden border border-forest/10 shadow-lg shadow-forest/10 h-[360px]">
                <iframe
                  title={`Zone d'intervention Embek Group à ${info.name}`}
                  src={`https://www.google.com/maps?q=${encodeURIComponent(info.name + ",+Belgique")}&z=12&hl=fr&output=embed`}
                  className="w-full h-full grayscale-[0.3]"
                  loading="lazy"
                  data-testid={`commune-map-${info.slug}`}
                />
              </div>
            </Reveal>
          </div>

          <div className="mt-20">
            <SectionHeading eyebrow="Nos prestations" title={`Que faisons-nous à ${info.name} ?`} />
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {services.map((s, i) => (
                <Reveal key={s.id} delay={(i % 3) * 0.06} className="h-full">
                  <Link
                    to="/services"
                    data-testid={`commune-service-${info.slug}-${i}`}
                    className="group h-full flex items-center gap-3.5 bg-white rounded-2xl border border-forest/10 px-5 py-4 shadow-sm hover:shadow-lg hover:shadow-forest/10 hover:-translate-y-0.5 transition-all duration-300"
                  >
                    <span className="w-8 h-8 rounded-full bg-moss/15 text-moss flex items-center justify-center shrink-0">
                      <Check size={14} strokeWidth={3} />
                    </span>
                    <span className="text-sm font-semibold text-forest">{s.title}</span>
                    <ArrowRight size={14} className="ml-auto text-moss opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>

          <div className="mt-20">
            <SectionHeading
              eyebrow="Communes voisines"
              title={`Nous intervenons aussi autour de ${info.name}`}
              description="Chaque commune dispose de sa page dédiée avec les informations sur nos interventions."
            />
            <Reveal delay={0.15}>
              <div className="mt-8 flex flex-wrap gap-2" data-testid={`commune-siblings-${info.slug}`}>
                {info.siblings.map((s) => (
                  <Link
                    key={s.slug}
                    to={`/jardinier-${s.slug}`}
                    data-testid={`commune-sibling-${s.slug}`}
                    className="px-3.5 py-1.5 rounded-full border border-forest/15 bg-white text-[12.5px] font-medium text-forest/80 flex items-center gap-1.5 hover:border-moss/50 hover:text-forest transition-colors"
                  >
                    <MapPin size={12} className="text-moss" /> {s.name}
                  </Link>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <CTABanner />
    </>
  );
}
